import type { AttendanceSummaryRow } from "@/lib/api/cccdAttendanceClient"

export type EventPhase = "live" | "upcoming" | "past"

/** Ruta del escaneo para un evento o regional: deep-linkable, reemplaza el selector. */
export function scanPath(row: Pick<AttendanceSummaryRow, "scope" | "id">): string {
  return `/cccd/checkin/${row.scope === "event" ? "event" : "regional"}/${row.id}`
}

export function reportPath(row: Pick<AttendanceSummaryRow, "scope" | "id">): string {
  return `/cccd/checkin/report/${row.scope === "event" ? "event" : "regional"}/${row.id}`
}

/** Fase del evento según sus fechas (YYYY-MM-DD) y el día de hoy; solo presentación. */
export function eventPhase(row: Pick<AttendanceSummaryRow, "eventStartDate" | "eventEndDate">, today: string): EventPhase {
  if (today < row.eventStartDate.slice(0, 10)) return "upcoming"
  if (today > row.eventEndDate.slice(0, 10)) return "past"
  return "live"
}

/** Día a abrir por defecto: hoy si cae en el rango; si no, el inicio (futuro) o el último día (pasado). */
export function defaultEventDay(range: { start: string; end: string } | null, today: string): string {
  if (!range) return today
  const start = range.start.slice(0, 10)
  const end = range.end.slice(0, 10)
  if (today < start) return start
  if (today > end) return end
  return today
}

/** Días (YYYY-MM-DD) entre inicio y fin, ambos inclusive; tope para que un rango absurdo no genere cientos de chips. */
export function daysBetween(start: string, end: string, max = 14): string[] {
  const out: string[] = []
  const cursor = new Date(`${start.slice(0, 10)}T00:00:00Z`)
  const last = new Date(`${end.slice(0, 10)}T00:00:00Z`)
  while (cursor <= last && out.length < max) {
    out.push(cursor.toISOString().slice(0, 10))
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return out
}

/** "2026-10-01" → "jue, 1 oct" (es-CO). Se ancla a mediodía para que la zona horaria no corra el día. */
export function formatDay(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("es-CO", { weekday: "short", day: "numeric", month: "short" })
}

/** "17 oct – 19 oct 2026"; un solo día si inicio y fin coinciden. */
export function formatRange(start: string, end: string): string {
  const fmt = (iso: string, withYear: boolean) =>
    new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("es-CO", { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) })
  if (start.slice(0, 10) === end.slice(0, 10)) return fmt(start, true)
  return `${fmt(start, false)} – ${fmt(end, true)}`
}

/** % de asistencia entero: boletos distintos con ingreso sobre boletos emitidos. 0 cuando no hay boletos. */
export function attendancePercent(attendeesCheckedIn: number, totalTickets: number): number {
  if (totalTickets <= 0) return 0
  return Math.round((attendeesCheckedIn / totalTickets) * 100)
}

export type SummaryTotals = Pick<
  AttendanceSummaryRow,
  "totalTickets" | "totalMembers" | "totalCompanions" | "checkedIn" | "attendeesCheckedIn" | "wristbands" | "checkedInToday"
>

/** Suma de la tabla para la fila de totales; la suma es de presentación, el dato de cada fila viene de la API. */
export function sumTotals(rows: SummaryTotals[]): SummaryTotals {
  return rows.reduce<SummaryTotals>(
    (acc, r) => ({
      totalTickets: acc.totalTickets + r.totalTickets,
      totalMembers: acc.totalMembers + r.totalMembers,
      totalCompanions: acc.totalCompanions + r.totalCompanions,
      checkedIn: acc.checkedIn + r.checkedIn,
      attendeesCheckedIn: acc.attendeesCheckedIn + r.attendeesCheckedIn,
      wristbands: acc.wristbands + r.wristbands,
      checkedInToday: acc.checkedInToday + r.checkedInToday,
    }),
    { totalTickets: 0, totalMembers: 0, totalCompanions: 0, checkedIn: 0, attendeesCheckedIn: 0, wristbands: 0, checkedInToday: 0 },
  )
}

/** Filtro compartido por el lanzador y el reporte: alcance + texto sobre nombre, región y slug. */
export function filterRows<T extends Pick<AttendanceSummaryRow, "scope" | "name" | "region" | "slug">>(
  rows: T[],
  scope: "all" | "event" | "regional",
  search: string,
): T[] {
  const q = search.trim().toLowerCase()
  return rows.filter(
    (r) =>
      (scope === "all" || r.scope === scope) &&
      (!q || `${r.name} ${r.region ?? ""} ${r.slug}`.toLowerCase().includes(q)),
  )
}

const LAST_SCOPE_KEY = "cccd-checkin-last-scope"

/** Último evento abierto en este dispositivo ("event:ID" / "regional:ID"); alimenta el botón "Continuar". */
export function readLastScope(): string {
  try {
    return localStorage.getItem(LAST_SCOPE_KEY) ?? ""
  } catch {
    return ""
  }
}

export function rememberScope(row: Pick<AttendanceSummaryRow, "scope" | "id">): void {
  try {
    localStorage.setItem(LAST_SCOPE_KEY, `${row.scope}:${row.id}`)
  } catch {
    // Sin almacenamiento: el evento sigue viviendo en la URL.
  }
}
